import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookOpenText, Send, ShieldCheck, Check } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollProgress } from "@/components/ScrollProgress";
import { CookieBanner } from "@/components/CookieBanner";
import { Field, PrivacyConsent } from "@/components/JoinForm";
import { STORY_IMAGE } from "@/data/media";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HowItWorks } from "@/components/HomeSections";
import { useSeo } from "@/hooks/useSeo";

const EMPTY = {
  nome: "",
  azienda: "",
  email: "",
  telefono: "",
  settore: "",
  messaggio: "",
  privacy: false,
};

export default function StoryPage() {
  useSeo({
    title:
      "Raccontaci la tua storia — DonnaImpresa | Storie di imprenditrici",
    description:
      "Racconta la tua storia di impresa a DonnaImpresa: obiettivi, ostacoli e traguardi diventano linee guida per altre donne. Compila il modulo e pubblicheremo la tua intervista nella Community.",
    path: "/racconta-la-tua-storia",
  });

  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      setError("Per procedere è necessario accettare l'informativa privacy.");
      toast.error("Per procedere è necessario accettare l'informativa privacy.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const { privacy, ...rest } = form;
      await api.post("/applications", {
        ...rest,
        tipo: "storia",
        privacy_consent: privacy,
      });
      setDone(true);
      setForm(EMPTY);
      toast.success("Grazie! Abbiamo ricevuto la tua storia.");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="story-page">
      <ScrollProgress />
      <Navbar />

      <main>
        <HowItWorks />
        <section className="grain relative overflow-hidden pt-36 sm:pt-40">
          <div className="pointer-events-none absolute -left-24 top-20 h-[320px] w-[320px] rounded-full bg-[#E0708A]/20 blur-[110px] di-blob" />
          <div className="relative mx-auto grid max-w-[1400px] items-center gap-10 px-5 pb-12 sm:px-10 lg:grid-cols-[1.05fr_0.95fr] lg:pb-16">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-[#6B5E62] di-underline"
                data-testid="story-back-home"
              >
                <ArrowLeft size={14} /> Home
              </Link>
              <motion.h1
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mt-8 font-display text-4xl leading-[1.03] tracking-tight text-[#1A1617] sm:text-5xl lg:text-6xl"
                data-testid="story-title"
              >
                Raccontaci
                <br />
                <span className="di-shine italic">la tua storia</span>.
              </motion.h1>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-[#6B5E62]">
                Ogni percorso d'impresa ha un momento in cui tutto sembrava
                impossibile. Raccontarlo serve a chi lo sta vivendo adesso.
                Pubblicheremo la tua storia nella pagina Community e sui nostri
                canali social.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Ti contattiamo per un'intervista breve, anche a distanza",
                  "Rivedi e approvi il testo prima della pubblicazione",
                  "La storia viene rilanciata su Instagram",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-[#6B5E62]">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E0708A]/15 text-[#E0708A]">
                      <Check size={12} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-[28px] border border-[#E8E2E4] shadow-[0_24px_70px_rgba(94,42,82,0.18)]"
            >
              <img
                src={STORY_IMAGE}
                alt="Imprenditrice che scrive i propri obiettivi alla scrivania"
                className="h-[300px] w-full object-cover object-[center_20%] sm:h-[420px]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-[900px] px-5 py-14 sm:px-8 lg:py-20">
          <div className="rounded-[26px] border border-[#E8E2E4] bg-white p-7 shadow-[0_14px_50px_rgba(94,42,82,0.07)] sm:p-10">
            <p className="inline-flex items-center gap-2 text-sm text-[#5E2A52]">
              <BookOpenText size={16} className="text-[#E0708A]" />
              Modulo <b>Raccontaci la tua storia</b>
            </p>

            <form onSubmit={submit} data-testid="story-form" className="mt-7 grid gap-4 sm:grid-cols-2">
              <Field required label="Nome e cognome *" testId="story-input-nome" value={form.nome} onChange={set("nome")} />
              <Field required label="Azienda / attività *" testId="story-input-azienda" value={form.azienda} onChange={set("azienda")} />
              <Field required type="email" label="Email *" testId="story-input-email" value={form.email} onChange={set("email")} />
              <Field label="Telefono" testId="story-input-telefono" value={form.telefono} onChange={set("telefono")} />
              <div className="sm:col-span-2">
                <Field label="Il tuo obiettivo" testId="story-input-settore" value={form.settore} onChange={set("settore")} />
              </div>
              <div className="sm:col-span-2">
                <Field
                  textarea
                  label="La tua storia *"
                  testId="story-input-messaggio"
                  required
                  value={form.messaggio}
                  onChange={set("messaggio")}
                />
              </div>
              <div className="sm:col-span-2">
                <PrivacyConsent
                  checked={form.privacy}
                  onChange={(v) => setForm({ ...form, privacy: v })}
                  testId="story-privacy-checkbox"
                />
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-5">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="story-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#E0708A] px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#5E2A52] disabled:opacity-60"
                >
                  {sending ? "Invio in corso..." : "Invia la mia storia"}
                  <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
                <p className="inline-flex items-center gap-2 text-xs text-[#6B5E62]">
                  <ShieldCheck size={14} className="text-[#5E2A52]" />
                  Dati conservati su server ad accesso riservato
                </p>
              </div>
              {error && (
                <p
                  className="sm:col-span-2 rounded-xl bg-red-50 px-5 py-3 text-sm text-red-700"
                  data-testid="story-error-message"
                >
                  {error}
                </p>
              )}
              {done && (
                <p
                  className="sm:col-span-2 rounded-xl bg-[#5E2A52] px-5 py-4 text-sm text-white"
                  data-testid="story-success-message"
                >
                  Grazie! Abbiamo ricevuto la tua storia: ti scriviamo presto per
                  l'intervista.
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
}
